import {
  SiderResizeHandle as CoreSiderResizeHandle,
  SIDER_WIDTH_STORAGE_KEY,
} from '@zhiyue/core';
import React from 'react';

export { SIDER_WIDTH_STORAGE_KEY };
export const DEFAULT_SIDER_WIDTH = 208;
export const MIN_SIDER_WIDTH = 180;
export const MAX_SIDER_WIDTH = 260;
export const clampSiderWidth = (width: number) =>
  Number.isFinite(width)
    ? Math.round(Math.min(MAX_SIDER_WIDTH, Math.max(MIN_SIDER_WIDTH, width)))
    : DEFAULT_SIDER_WIDTH;

export const getStoredSiderWidth = () => {
  if (typeof window === 'undefined') return DEFAULT_SIDER_WIDTH;
  try {
    const raw = window.localStorage.getItem(SIDER_WIDTH_STORAGE_KEY);
    if (!raw?.trim()) return DEFAULT_SIDER_WIDTH;
    return clampSiderWidth(Number(raw));
  } catch {
    return DEFAULT_SIDER_WIDTH;
  }
};

const SiderResizeHandle: React.FC<
  React.ComponentProps<typeof CoreSiderResizeHandle>
> = (props) => {
  const host = React.useRef<HTMLDivElement>(null);
  const onResizeWidth = React.useCallback(
    (width: number) => {
      const nextWidth = clampSiderWidth(width);
      try {
        window.localStorage.setItem(SIDER_WIDTH_STORAGE_KEY, String(nextWidth));
      } catch {
        /* Resizing still works without persistent storage. */
      }
      props.onResizeWidth(nextWidth);
    },
    [props.onResizeWidth],
  );

  React.useEffect(() => {
    const sync = () => {
      const slider =
        host.current?.querySelector<HTMLElement>('[role="slider"]');
      if (!slider) return;
      if (slider.getAttribute('aria-valuemax') !== String(MAX_SIDER_WIDTH)) {
        slider.setAttribute('aria-valuemax', String(MAX_SIDER_WIDTH));
        slider.setAttribute('aria-orientation', 'horizontal');
      }
    };
    sync();
    const observer = new MutationObserver(sync);
    if (host.current)
      observer.observe(host.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [props.enabled]);

  return (
    <div ref={host} className="app-sider-resize">
      <CoreSiderResizeHandle {...props} onResizeWidth={onResizeWidth} />
    </div>
  );
};

export default SiderResizeHandle;
